import type { Dispatch, SetStateAction } from "react";
import type { ElementCategory } from "../model/inp/properties.ts";
import type { TextKey } from "../i18n/en.ts";
import type { Translate } from "../i18n/hooks.ts";
import type { EditMode } from "./editModeTools.ts";
import type { DataTab } from "./ProjectDataDialog.tsx";
import type { useDialogToggles } from "./useDialogToggles.ts";
import type { useThematicMap } from "./useThematicMap.ts";
import type { useMapQuery } from "./useMapQuery.ts";
import type { TraceDirection } from "./useNetworkTrace.ts";
import { EditGeometryIcon } from "./EditGeometryIcon.tsx";
import { MultiSelectIcon } from "./MultiSelectIcon.tsx";
import { SelectionToolsMenu } from "./SelectionToolsMenu.tsx";
import { ToolsMenu } from "./ToolsMenu.tsx";
import { AddElementMenu } from "./AddElementMenu.tsx";
import { ImportIcon } from "./ImportIcon.tsx";
import { MeasureIcon } from "./MeasureIcon.tsx";
import { DefaultsIcon } from "./DefaultsIcon.tsx";
import { ProjectDataMenu } from "./ProjectDataMenu.tsx";
import { ProfileIcon } from "./ProfileIcon.tsx";
import { ThematicMapIcon } from "./ThematicMapIcon.tsx";
import { MapQueryIcon } from "./MapQueryIcon.tsx";
import { IconButton } from "./IconButton.tsx";
import { TOOLBAR_MENU_GROUP } from "./toolbarMenuGroup.ts";

/**
 * Which of the rail's own toggle-tools is currently engaged, if any — one
 * `TextKey`, first match wins. Deliberately excludes the "add junction/
 * conduit/subcatchment..." family (`ADD_LINK`/`ADD_POINT`/
 * `add-subcatchment` in App.tsx's own `mode`): those already get their own
 * live instructions via `.map-hint-banner`, directly over the map — showing
 * the exact same "you're adding a junction" text a second time next to the
 * rail would be noise, not help. Order here isn't a real priority ranking
 * (most of these are mutually exclusive by construction anyway — e.g.
 * `measuring` only applies in geo mode, `mode` only ever holds one value at
 * once) — it's just "most specific/deliberate mode first" for the rare
 * genuine overlap.
 */
function activeToolLabelKey({
  mode,
  editGeometry,
  multiSelectMode,
  measuring,
  mapQueryOpen,
}: {
  mode: string;
  editGeometry: boolean;
  multiSelectMode: boolean;
  measuring: boolean;
  mapQueryOpen: boolean;
}): TextKey | null {
  if (measuring) return "toolbar.measureDistance";
  if (mode === "select-rect") return "selectionTools.rectangle";
  if (mode === "select-lasso") return "selectionTools.lasso";
  if (mode === "select" && editGeometry) return "toolbar.editGeometry";
  if (multiSelectMode) return "toolbar.multiSelectMode";
  if (mapQueryOpen) return "toolbar.mapQuery";
  // Audit finding: opening the thematic map used to paint both the
  // rail button in its own active-teal state AND this "Mapa temático"
  // pill just above the map — visibly redundant. ThematicMapBar's own
  // header/legend already anchors the current theme on-screen with
  // strong presence, so the extra pill added nothing the bar (plus the
  // active button) didn't already say. Other rail tools that share
  // this label surface (measure/selection shapes/edit-geometry/multi-
  // select/map-query) kept it: some of them have no separate
  // on-screen bar at all, and the audit only called out this one case.
  return null;
}

/**
 * The vertical strip of editing tools, docked to the left of the element
 * tree/map — UI redesign phase 3. Split out of the single AppToolbar.tsx
 * (491 lines, 47 props): everything here is a MAP-EDITING tool (select,
 * draw, measure, query, thematic), as opposed to AppBar.tsx's own
 * app-identity/project/run-simulation controls. Every control here is the
 * exact same one AppToolbar.tsx used to render inline — same aria-labels/
 * titles (so the e2e suite's own `getByRole('button', {name})` lookups
 * don't need to change), same behavior, just laid out vertically and
 * grouped by what they're FOR rather than left in one long undifferentiated
 * row. Purely a rendering shell, same as AppBar.tsx — every piece of
 * state/behavior it needs is a prop, still owned and derived by App.tsx's
 * own hooks.
 */
export function ToolRail({
  t,
  dialogs,
  mode,
  editGeometry,
  setEditGeometry,
  onChangeMode,
  multiSelectMode,
  setMultiSelectMode,
  onSelectByType,
  onOpenQuerySelect,
  mapMode,
  measuring,
  onToggleMeasure,
  setProjectDataTab,
  onOpenLongitudinalProfile,
  thematicMap,
  onToggleThematicMap,
  mapQuery,
  onStartTrace,
}: {
  t: Translate;
  dialogs: ReturnType<typeof useDialogToggles>;
  mode: string;
  editGeometry: boolean;
  setEditGeometry: Dispatch<SetStateAction<boolean>>;
  onChangeMode: (mode: EditMode) => void;
  multiSelectMode: boolean;
  setMultiSelectMode: Dispatch<SetStateAction<boolean>>;
  onSelectByType: (category: ElementCategory, kind: string) => void;
  onOpenQuerySelect: () => void;
  mapMode: "plane" | "geo";
  /** Only usable/shown in geo mode — a real-distance measurement is meaningless against the plane view's own arbitrary local drawing coordinates. */
  measuring: boolean;
  onToggleMeasure: () => void;
  setProjectDataTab: (tab: DataTab) => void;
  onOpenLongitudinalProfile: () => void;
  thematicMap: ReturnType<typeof useThematicMap>;
  onToggleThematicMap: () => void;
  mapQuery: ReturnType<typeof useMapQuery>;
  onStartTrace: (direction: TraceDirection) => void;
}) {
  const activeLabelKey = activeToolLabelKey({
    mode,
    editGeometry,
    multiSelectMode,
    measuring,
    mapQueryOpen: mapQuery.open,
  });
  return (
    <>
      <nav className="tool-rail" aria-label={t("toolRail.label")}>
        <div className="tool-rail-group">
          <IconButton
            className={
              multiSelectMode ? "tool-rail-button tool-rail-button-active" : "tool-rail-button"
            }
            icon={<MultiSelectIcon />}
            label={t("toolbar.multiSelectMode")}
            onClick={() => setMultiSelectMode((enabled) => !enabled)}
          />
          <SelectionToolsMenu
            mode={mode}
            onSelectShapeTool={onChangeMode}
            onSelectByType={onSelectByType}
            onOpenQuerySelect={onOpenQuerySelect}
            open={dialogs.open.selectionToolsMenu}
            onToggle={() => dialogs.toggleExclusive("selectionToolsMenu", TOOLBAR_MENU_GROUP)}
            onClose={() => dialogs.close("selectionToolsMenu")}
          />
        </div>

        <div className="tool-rail-divider" />

        <div className="tool-rail-group">
          <AddElementMenu
            mode={mode}
            onSelectTool={onChangeMode}
            open={dialogs.open.addElementMenu}
            onToggle={() => dialogs.toggleExclusive("addElementMenu", TOOLBAR_MENU_GROUP)}
            onClose={() => dialogs.close("addElementMenu")}
          />
          <IconButton
            className="tool-rail-button"
            icon={<ImportIcon />}
            label={t("projectMenu.importLayers")}
            onClick={() => dialogs.openDialog("gisImport")}
          />
          <IconButton
            className={
              mode === "select" && editGeometry
                ? "tool-rail-button tool-rail-button-active"
                : "tool-rail-button"
            }
            icon={<EditGeometryIcon />}
            label={t("toolbar.editGeometry")}
            onClick={() => {
              if (mode !== "select") {
                onChangeMode("select");
                setEditGeometry(true);
              } else {
                setEditGeometry((enabled) => !enabled);
              }
            }}
          />
        </div>

        <div className="tool-rail-divider" />

        <div className="tool-rail-group">
          {mapMode === "geo" && (
            <IconButton
              className={
                measuring ? "tool-rail-button tool-rail-button-active" : "tool-rail-button"
              }
              icon={<MeasureIcon />}
              label={t("toolbar.measureDistance")}
              onClick={onToggleMeasure}
            />
          )}
          <IconButton
            className={
              mapQuery.open ? "tool-rail-button tool-rail-button-active" : "tool-rail-button"
            }
            icon={<MapQueryIcon />}
            label={t("toolbar.mapQuery")}
            onClick={onOpenQuerySelect}
          />
          {/* Audit finding: the ToolsMenu that used to live here carried
              only "trace upstream/downstream from a node" — two items
              always visible but only meaningful once a node was picked
              afterward. Those two ALSO moved to the PropertyPanel's own
              contextual actions row for an already-selected node, leaving
              this slot empty for a while — brought back deliberately, as a
              standing home for future tools (ToolsMenu.tsx's own header),
              not just the panel shortcut. */}
          <ToolsMenu
            onStartTrace={onStartTrace}
            open={dialogs.open.toolsMenu}
            onToggle={() => dialogs.toggleExclusive("toolsMenu", TOOLBAR_MENU_GROUP)}
            onClose={() => dialogs.close("toolsMenu")}
          />
        </div>

        <div className="tool-rail-divider" />

        <div className="tool-rail-group">
          <IconButton
            className="tool-rail-button"
            icon={<DefaultsIcon />}
            label={t("projectMenu.elementDefaults")}
            onClick={() => dialogs.openDialog("elementDefaults")}
          />
          <ProjectDataMenu
            open={dialogs.open.projectDataMenu}
            onToggle={() => dialogs.toggleExclusive("projectDataMenu", TOOLBAR_MENU_GROUP)}
            onClose={() => dialogs.close("projectDataMenu")}
            onSelectTab={setProjectDataTab}
          />
          <IconButton
            className="tool-rail-button"
            icon={<ProfileIcon />}
            label={t("toolbar.longitudinalProfile")}
            onClick={onOpenLongitudinalProfile}
          />
          <IconButton
            className={
              thematicMap.open ? "tool-rail-button tool-rail-button-active" : "tool-rail-button"
            }
            icon={<ThematicMapIcon />}
            label={t("toolbar.thematicMap")}
            onClick={onToggleThematicMap}
          />
        </div>
      </nav>
      {/* Rendered as `.tool-rail`'s own SIBLING (not a child of it) —
          `.tool-rail` itself sets `overflow-y: auto`, which per the CSS
          spec also computes its own `overflow-x` as `auto` (not the
          `visible` it'd otherwise default to) the moment either axis is
          set to anything but `visible` — a `position: absolute; left:
          100%` child would get clipped/scrolled inside the rail's own 56px
          box instead of floating past it. Positioned against
          `.workspace-body` instead (`position: relative`, UI redesign
          phase 4), which the rail is already a direct child of. */}
      {activeLabelKey && (
        <div className="tool-rail-active-label" role="status">
          {t(activeLabelKey)}
        </div>
      )}
    </>
  );
}
